import { Text } from "components/text";
import type { FacetResult } from "hooks/search/types";
import { FilterSectionAction } from "./FilterSectionAction";
import {
  getThemaCodeKind,
  ThemaCodeFilterSectionRow,
} from "./ThemaCodeFilterSectionRow";

export type ThemaFacetItem = Pick<
  FacetResult["counts"][number],
  "value" | "label" | "kind"
>;

export { getThemaCodeKind };

function isThemaSubject(item: ThemaFacetItem): boolean {
  return getThemaCodeKind(item.value, item.kind).toLowerCase() === "subject";
}

export function sortThemaCodeItems(items: ThemaFacetItem[]): ThemaFacetItem[] {
  const sorted = [...items].sort((a, b) =>
    a.value.localeCompare(b.value, undefined, { sensitivity: "base" }),
  );
  const subjects = sorted.filter(isThemaSubject);
  const qualifiers = sorted.filter((item) => !isThemaSubject(item));

  return [...subjects, ...qualifiers];
}

export function ThemaCodeFilterSection({
  label,
  actionLabel,
  actionDisabled,
  actionDisabledTooltip,
  items,
  selectedValues,
  name,
  noSuggestionsFound,
  onAction,
  onToggle,
}: {
  label: string;
  actionLabel?: string;
  actionDisabled?: boolean;
  actionDisabledTooltip?: string;
  items: ThemaFacetItem[];
  selectedValues: string[];
  name: string;
  noSuggestionsFound?: string;
  onAction?: () => void;
  onToggle: (value: string, checked: boolean) => void;
}) {
  if (!items.length && !noSuggestionsFound) return null;

  const action = actionLabel && onAction && (
    <FilterSectionAction
      label={actionLabel}
      disabled={actionDisabled || !!noSuggestionsFound}
      disabledTooltip={actionDisabled ? actionDisabledTooltip : undefined}
      onClick={onAction}
    />
  );

  return (
    <div className="flex flex-col gap-y-2 py-2">
      <div className="flex items-center justify-between px-6">
        <Text variant="muted">{label}</Text>
        {action}
      </div>
      {noSuggestionsFound ? (
        <Text
          as="div"
          variant="body"
          className="flex w-full justify-center text-center py-2 px-6"
        >
          {noSuggestionsFound}
        </Text>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          {items.map((item) => (
            <ThemaCodeFilterSectionRow
              key={item.value}
              item={item}
              name={name}
              checked={selectedValues.includes(item.value)}
              onCheckedChange={(checked) => onToggle(item.value, checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
